import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSortAmountDown,
  faSortAmountUp,
  faEye,
  faGear,
  faPaperPlane,
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../config';
import ClaimsProcessingDetailsModal from './components/ClaimsProcessingDetailsModal';
import ProcessClaimModal from './components/ProcessClaimModal';

const TABS = [
  { id: 'batch-for-processing', label: 'Batch For Processing' },
  { id: 'for-processing', label: 'For Processing' },
  { id: 'processed', label: 'Processed' },
  { id: 'extracted', label: 'Extracted' },
  { id: 'paid', label: 'Paid' },
  { id: 'rtp', label: 'RTP' },
  { id: 'from-provider-access', label: 'From Provider Access' },
];

const CLAIM_SORT_OPTIONS = [
  { value: 'loa_no', label: 'LOA No.' },
  { value: 'trans_no', label: 'TRN No.' },
  { value: 'member_name', label: 'Member Name' },
  { value: 'corp_name', label: 'Corporate' },
  { value: 'batch_no', label: 'Batch No.' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'received_date', label: 'Received Date' },
  { value: 'trans_date', label: 'Transaction Date' },
  { value: 'provider_name', label: 'Provider Name' },
  { value: 'provider_code', label: 'Provider Code' },
  { value: 'coverage_code', label: 'Coverage Code' },
  { value: 'amount', label: 'LOA Amount' },
  { value: 'c_amount', label: 'Claim Amount' },
  { value: 'status', label: 'Status' },
  { value: 'plan_code', label: 'Plan Code' },
  { value: 'soa_no', label: 'SOA No.' },
  { value: 'doctor_name', label: 'Doctor Name' },
  { value: 'chief_complaint', label: 'Chief Complaint' },
  { value: 'rtp_tag', label: 'RTP Tag' },
  { value: 'trans_flag_status', label: 'Trans Flag Status' },
  { value: 'claim_processor', label: 'Claim Processor' },
  { value: 'claim_processed_date', label: 'Claim Processed Date' },
  { value: 'claim_remarks', label: 'Claim Remarks' },
  { value: 'bill_no', label: 'Bill No.' },
  { value: 'diagnosis', label: 'Diagnosis' },
  { value: 'spec_transmittal', label: 'Spec Transmittal' },
  { value: 'stale_tag', label: 'Stale Tag' },
];

const BATCH_SORT_OPTIONS = [
  { value: 'received_date', label: 'Received Date' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'batch_no', label: 'Batch No.' },
  { value: 'provider_name', label: 'Provider Name' },
  { value: 'soa_amount', label: 'SOA Amount' },
  { value: 'claim_count', label: 'Claim Count' },
  { value: 'attached_loa_count', label: 'Attached LOA Count' },
  { value: 'for_processing_count', label: 'For Processing Count' },
  { value: 'for_rtp_count', label: 'For RTP Count' },
  { value: 'saved_count', label: 'Saved Count' },
  { value: 'ready_count', label: 'Ready Count' },
  { value: 'batch_processing_status', label: 'Processing Status' },
];

const CLAIM_COLUMNS = [
  { key: 'actions_start', label: 'Actions' },
  { key: 'batch_no', label: 'Batch No.' },
  { key: 'loa_no', label: 'LOA No.' },
  { key: 'trans_no', label: 'TRN No.' },
  { key: 'member_name', label: 'Member Name' },
  { key: 'corp_name', label: 'Corporate' },
  { key: 'provider_name', label: 'Provider' },
  { key: 'coverage_code', label: 'Coverage' },
  { key: 'amount', label: 'LOA Amount' },
  { key: 'c_amount', label: 'Claim Amount' },
  { key: 'received_date', label: 'Received Date' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'trans_date', label: 'Trans Date' },
  { key: 'soa_no', label: 'SOA No.' },
  { key: 'doctor_name', label: 'Doctor' },
  { key: 'status', label: 'Status' },
  { key: 'stale_tag', label: 'Stale' },
  { key: 'actions_end', label: 'Actions' },
];

const BATCH_COLUMNS = [
  { key: 'actions', label: 'Actions' },
  { key: 'batch_no', label: 'Batch No.' },
  { key: 'provider_name', label: 'Provider' },
  { key: 'received_date', label: 'Received Date' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'soa_no', label: 'SOA No.' },
  { key: 'soa_amount', label: 'SOA Amount' },
  { key: 'claim_count', label: 'Claim Count' },
  { key: 'attached_loa_count', label: 'Attached LOA' },
  { key: 'for_processing_count', label: 'For Processing' },
  { key: 'saved_count', label: 'Saved' },
  { key: 'for_rtp_count', label: 'For RTP' },
  { key: 'pending_rtp_entry_count', label: 'Pending RTP Approval' },
  { key: 'ready_count', label: 'Ready' },
  { key: 'batch_processing_status', label: 'Status' },
];

const TAB_ENDPOINTS = {
  'batch-for-processing': '/claims/processing/batch-for-processing',
  'for-processing': '/claims/processing/for-processing',
  processed: '/claims/processing/processed',
  extracted: '/claims/processing/extracted',
  paid: '/claims/processing/paid',
  rtp: '/claims/processing/rtp',
  'from-provider-access': '/claims/processing/from-provider-access',
};

const ClaimsProcessing = () => {
  const [activeTab, setActiveTab] = useState('batch-for-processing');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    due_date_from: '',
    due_date_to: '',
    received_date_from: '',
    received_date_to: '',
    loa_date_from: '',
    loa_date_to: '',
    loa_no: '',
    trans_no: '',
    batch_no: '',
    provider_name: '',
    coverage_code: '',
    batch_processing_status: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [sortBy, setSortBy] = useState('received_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [processModalOpen, setProcessModalOpen] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);
  const [processMode, setProcessMode] = useState('edit');
  const [showReturnButton, setShowReturnButton] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBatchNo, setSelectedBatchNo] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [batchDetailLoading, setBatchDetailLoading] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchSubmitAlert, setBatchSubmitAlert] = useState(null);

  const isBatchTab = activeTab === 'batch-for-processing';
  const activeSortOptions = isBatchTab ? BATCH_SORT_OPTIONS : CLAIM_SORT_OPTIONS;

  const phpFormatter = useMemo(() => new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }), []);

  useEffect(() => {
    fetchClaims();
  }, [activeTab, currentPage, itemsPerPage, searchTerm, sortBy, sortDirection, filters]);

  const resolveApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message;
    if (!message) return fallback;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'object') return Object.values(message).flat().join(', ');
    return fallback;
  };

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const endpoint = TAB_ENDPOINTS[activeTab] || TAB_ENDPOINTS['for-processing'];
      const response = await axios.get(`${BASE_URL}${endpoint}`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortBy,
          sort_direction: sortDirection,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      const resource = response.data?.resource;
      const normalizedRows = Array.isArray(resource) ? resource : Object.values(resource || {});
      const total = response.data?._total_count || response.data?.pagination?.total || 0;
      setRows(normalizedRows);
      setTotalEntries(total);
      setTotalPages(itemsPerPage > 0 ? Math.max(1, Math.ceil(total / itemsPerPage)) : 1);
    } catch (error) {
      if (error.response?.status === 401) {
        window.location.href = '/login';
        return;
      }

      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      } else {
        toast.error(resolveApiErrorMessage(error, 'Failed to fetch claims.'), { duration: 4000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchDetail = async (batchNo = selectedBatchNo) => {
    if (!batchNo) return;
    setBatchDetailLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/claims/processing/batch-for-processing/${encodeURIComponent(batchNo)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      setSelectedBatchNo(batchNo);
      setBatchDetail(response.data?.data || null);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, 'Failed to fetch batch details.'), {
        duration: 4000,
      });
    } finally {
      setBatchDetailLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setRows([]);
    setCurrentPage(1);
    setSelectedBatchNo(null);
    setBatchDetail(null);
    setSortBy(tabId === 'batch-for-processing' ? 'received_date' : 'trans_date');
    setSortDirection('desc');
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleExportCsv = async () => {
    if (isBatchTab) return;

    try {
      const response = await axios.get(`${BASE_URL}/claims/processing/export`, {
        params: {
          tab: activeTab,
          search: searchTerm,
          sort_by: sortBy,
          sort_direction: sortDirection,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `claims_${activeTab}_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export CSV.', { duration: 4000 });
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-emerald-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const getShowingEntriesText = () => {
    if (totalEntries === 0) return 'Showing 0 to 0 of 0 entries';
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} entries`;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return format(date, 'yyyy-MM-dd');
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return '-';
    return phpFormatter.format(parsed);
  };

  const getBatchStatusClass = (status) => {
    const normalized = String(status || '').trim().toUpperCase();
    if (normalized === 'SUBMITTED') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (normalized === 'IN_PROGRESS') return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getClaimStatusClass = (row) => {
    const claimStatus = String(row.claim_status || '').trim().toUpperCase();
    if (Number(row.rtp_tag || 0) === 1 || claimStatus === 'FOR RTP') {
      return 'bg-red-50 text-red-700 border-red-200';
    }
    if (claimStatus === 'FOR PROCESSING') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (claimStatus === 'PROCESSED') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const openViewModal = (claim) => {
    setActiveClaim(claim);
    setViewModalOpen(true);
  };

  const openProcessModal = (claim) => {
    const isEditTab = ['for-processing', 'from-provider-access'].includes(activeTab);
    setProcessMode(isEditTab ? 'edit' : 'view');
    setShowReturnButton(activeTab === 'from-provider-access');
    setActiveClaim(claim);
    setProcessModalOpen(true);
  };

  const openBatchDetail = async (batch) => {
    await fetchBatchDetail(batch.batch_no);
  };

  const closeBatchDetail = () => {
    setSelectedBatchNo(null);
    setBatchDetail(null);
  };

  const openBatchClaim = (row, mode = 'edit') => {
    setProcessMode(mode);
    setShowReturnButton(false);
    setActiveClaim({
      ...row,
      amount: row.loa_amount,
      c_amount: row.draft_claim_amount ?? row.claim_amount,
      status: row.claim_status,
    });
    setProcessModalOpen(true);
  };

  const openRtpEntry = (row) => {
    const params = new URLSearchParams({
      batch_no: row.batch_no || selectedBatchNo || '',
      trans_no: row.trans_no || '',
      loa_no: row.loa_no || '',
    });
    window.open(`/claims/rtp-entry?${params.toString()}`, '_blank');
  };

  const handleBatchClaimSaved = async () => {
    await fetchBatchDetail(selectedBatchNo);
    await fetchClaims();
  };

  const buildBatchSubmitAlert = (details = null) => {
    const summary = details?.summary || batchDetail?.summary || {};
    const claims = batchDetail?.claims || [];
    const apiBlockingRows = Array.isArray(details?.blocking_rows) ? details.blocking_rows : [];
    const localBlockingRows = claims
      .filter((row) => Number(row.ready_for_batch_submit || 0) !== 1)
      .map((row) => ({
        trans_no: row.trans_no,
        loa_no: row.loa_no,
        claim_status: row.claim_status,
        rtp_approval_status: row.latest_rtp_entry_status,
        blocking_reason: row.blocking_reason || 'Claim is not ready for batch submit.',
      }));
    const blockingRows = apiBlockingRows.length > 0 ? apiBlockingRows : localBlockingRows;
    const issues = [];
    const mismatch = details?.mismatch || (summary.soa_amount_matches === false
      ? {
          soa_amount: summary.soa_amount,
          expected_amount: summary.soa_expected_amount ?? summary.computed_soa_total,
          difference: summary.soa_difference,
        }
      : null);

    if (summary.claim_count_matches === false) {
      issues.push('Claim Count must match the attached LOA count.');
    }

    if (summary.soa_amount_matches === false) {
      issues.push(
        `SOA Amount must match ${summary.soa_validation_label || 'Total Claim Amount'}.`
      );
    }

    if (blockingRows.length > 0) {
      issues.push('All claims must be ready. For RTP claims need Approved Final RTP Approval.');
    }

    if (issues.length === 0) {
      issues.push('Batch is not ready for submit. Refresh the batch details and review pending rows.');
    }

    return {
      title: 'Batch Cannot Be Submitted Yet',
      message: details?.message || 'Complete the required actions before submitting this batch.',
      issues,
      blockingRows,
      mismatch,
    };
  };

  const handleSubmitBatch = async () => {
    const summary = batchDetail?.summary;
    const batchNo = batchDetail?.batch?.batch_no || selectedBatchNo;
    if (!batchNo) {
      toast.error('Batch number is missing.', { duration: 4000 });
      return;
    }

    if (!summary?.can_submit) {
      setBatchSubmitAlert(buildBatchSubmitAlert());
      return;
    }

    const confirmed = window.confirm('Submit this batch for processing?');
    if (!confirmed) return;

    setBatchSubmitting(true);
    try {
      await axios.post(
        `${BASE_URL}/claims/processing/batch-for-processing/submit`,
        { batch_no: batchNo },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      toast.success('Batch submitted for processing successfully.', { duration: 4000 });
      await fetchBatchDetail(batchNo);
      await fetchClaims();
    } catch (error) {
      const data = error.response?.data || {};
      const mismatch = data.mismatch || data.details?.mismatch;
      const mismatchText = mismatch
        ? ` SOA: ${formatAmount(mismatch.soa_amount)}, Expected: ${formatAmount(mismatch.expected_amount)}, Difference: ${formatAmount(mismatch.difference)}.`
        : '';
      if (data.details) {
        setBatchSubmitAlert(buildBatchSubmitAlert(data.details));
      }
      toast.error(`${resolveApiErrorMessage(error, 'Failed to submit batch.')}${mismatchText}`, {
        duration: 8000,
      });
    } finally {
      setBatchSubmitting(false);
    }
  };

  const closeModals = () => {
    setViewModalOpen(false);
    setProcessModalOpen(false);
    setActiveClaim(null);
  };

  const closeBatchSubmitAlert = () => {
    setBatchSubmitAlert(null);
  };

  const renderFilters = () => (
    <div className="mt-4 bg-slate-50 border border-slate-100 rounded-2xl p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Due Date From</span>
          <input
            type="date"
            value={filters.due_date_from}
            onChange={(e) => handleFilterChange('due_date_from', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Due Date To</span>
          <input
            type="date"
            value={filters.due_date_to}
            onChange={(e) => handleFilterChange('due_date_to', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Received Date From</span>
          <input
            type="date"
            value={filters.received_date_from}
            onChange={(e) => handleFilterChange('received_date_from', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Received Date To</span>
          <input
            type="date"
            value={filters.received_date_to}
            onChange={(e) => handleFilterChange('received_date_to', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
          />
        </label>
        {!isBatchTab && (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">LOA Date From</span>
              <input
                type="date"
                value={filters.loa_date_from}
                onChange={(e) => handleFilterChange('loa_date_from', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">LOA Date To</span>
              <input
                type="date"
                value={filters.loa_date_to}
                onChange={(e) => handleFilterChange('loa_date_to', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">LOA Number</span>
              <input
                type="text"
                value={filters.loa_no}
                onChange={(e) => handleFilterChange('loa_no', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                placeholder="LOA Number"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">TRN Number</span>
              <input
                type="text"
                value={filters.trans_no}
                onChange={(e) => handleFilterChange('trans_no', e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                placeholder="TRN Number"
              />
            </label>
          </>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Batch Number</span>
          <input
            type="text"
            value={filters.batch_no}
            onChange={(e) => handleFilterChange('batch_no', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            placeholder="Batch Number"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-slate-500">Provider Name</span>
          <input
            type="text"
            value={filters.provider_name}
            onChange={(e) => handleFilterChange('provider_name', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            placeholder="Provider Name"
          />
        </label>
        {!isBatchTab && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-500">Coverage</span>
            <input
              type="text"
              value={filters.coverage_code}
              onChange={(e) => handleFilterChange('coverage_code', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              placeholder="Coverage"
            />
          </label>
        )}
        {isBatchTab && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-slate-500">Processing Status</span>
            <select
              value={filters.batch_processing_status}
              onChange={(e) => handleFilterChange('batch_processing_status', e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
            >
              <option value="">All</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="SUBMITTED">Submitted</option>
            </select>
          </label>
        )}
      </div>
    </div>
  );

  const renderBatchListTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full table-auto">
        <thead className="bg-slate-50">
          <tr>
            {BATCH_COLUMNS.map((column) => (
              <th
                key={column.key}
                className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            Array(itemsPerPage)
              .fill(null)
              .map((_, i) => (
                <tr key={`batch-loading-${i}`} className="animate-pulse">
                  {BATCH_COLUMNS.map((col) => (
                    <td key={`${col.key}-${i}`} className="p-3">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                </tr>
              ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={BATCH_COLUMNS.length} className="p-6 text-center text-gray-500 text-sm">
                No batches found.
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const submittedLocked = Number(row.is_submitted_locked || 0) === 1;
              return (
                <tr key={row.batch_no} className="hover:bg-slate-50">
                  <td className="p-3">
                    <button
                      onClick={() => openBatchDetail(row)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                        submittedLocked
                          ? 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                          : 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                      }`}
                    >
                      <FontAwesomeIcon icon={submittedLocked ? faEye : faGear} />
                      {submittedLocked ? 'View' : 'Start Process'}
                    </button>
                  </td>
                  <td className="p-3 text-sm font-semibold text-slate-700">{row.batch_no || '-'}</td>
                  <td className="p-3 text-sm">{row.provider_name || '-'}</td>
                  <td className="p-3 text-sm">{formatDate(row.received_date)}</td>
                  <td className="p-3 text-sm">{formatDate(row.due_date)}</td>
                  <td className="p-3 text-sm">{row.soa_no || '-'}</td>
                  <td className="p-3 text-sm">{formatAmount(row.soa_amount)}</td>
                  <td className="p-3 text-sm">{row.claim_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.attached_loa_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.for_processing_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.saved_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.for_rtp_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.pending_rtp_entry_count ?? '-'}</td>
                  <td className="p-3 text-sm">{row.ready_count ?? '-'}</td>
                  <td className="p-3 text-sm">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getBatchStatusClass(row.batch_processing_status)}`}>
                      {row.batch_processing_status || 'PENDING'}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const renderClaimRowsTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full table-auto">
        <thead className="bg-slate-50">
          <tr>
            {CLAIM_COLUMNS.map((column) => (
              <th
                key={column.key}
                className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            Array(itemsPerPage)
              .fill(null)
              .map((_, i) => (
                <tr key={`loading-${i}`} className="animate-pulse">
                  {CLAIM_COLUMNS.map((col) => (
                    <td key={`${col.key}-${i}`} className="p-3">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                </tr>
              ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={CLAIM_COLUMNS.length} className="p-6 text-center text-gray-500 text-sm">
                No claims found.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const isEditTab = ['for-processing', 'from-provider-access'].includes(activeTab);
              return (
                <tr key={`${row.trans_no}-${index}`} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openViewModal(row)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => openProcessModal(row)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                          isEditTab
                            ? 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                            : 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                        }`}
                      >
                        <FontAwesomeIcon icon={isEditTab ? faGear : faEye} />
                      </button>
                    </div>
                  </td>
                  <td className="p-3 text-sm">{row.batch_no || '-'}</td>
                  <td className="p-3 text-sm">{row.loa_no || '-'}</td>
                  <td className="p-3 text-sm">{row.trans_no || '-'}</td>
                  <td className="p-3 text-sm">{row.member_name || '-'}</td>
                  <td className="p-3 text-sm">{row.corp_name || '-'}</td>
                  <td className="p-3 text-sm">{row.provider_name || '-'}</td>
                  <td className="p-3 text-sm">{row.coverage_code || '-'}</td>
                  <td className="p-3 text-sm">{formatAmount(row.amount)}</td>
                  <td className="p-3 text-sm">{formatAmount(row.c_amount)}</td>
                  <td className="p-3 text-sm">{formatDate(row.received_date)}</td>
                  <td className="p-3 text-sm">{formatDate(row.due_date)}</td>
                  <td className="p-3 text-sm">{formatDate(row.trans_date)}</td>
                  <td className="p-3 text-sm">{row.soa_no || '-'}</td>
                  <td className="p-3 text-sm">{row.doctor_name || '-'}</td>
                  <td className="p-3 text-sm">
                    {row.status ? (
                      <span dangerouslySetInnerHTML={{ __html: row.status }} />
                    ) : (
                      row.claim_status || '-'
                    )}
                  </td>
                  <td className="p-3 text-sm">{row.stale_tag || '-'}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openViewModal(row)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => openProcessModal(row)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                          isEditTab
                            ? 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                            : 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                        }`}
                      >
                        <FontAwesomeIcon icon={isEditTab ? faGear : faEye} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  const renderPagination = () => (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
      <div className="flex items-center gap-4 mb-3 md:mb-0">
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
        >
          {[5, 10, 20, 50, 100].map((val) => (
            <option key={val} value={val}>
              {val} per page
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-600">{getShowingEntriesText()}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || loading}
          className="p-2 text-gray-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-all"
          aria-label="First page"
        >
          <FontAwesomeIcon icon={faAngleDoubleLeft} />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className="p-2 text-gray-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-all"
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>
        {renderPageNumbers()}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          className="p-2 text-gray-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-all"
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
          className="p-2 text-gray-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-all"
          aria-label="Last page"
        >
          <FontAwesomeIcon icon={faAngleDoubleRight} />
        </button>
      </div>
    </div>
  );

  const renderBatchSummaryCards = () => {
    const summary = batchDetail?.summary || {};
    const soaValidationLabel = summary.soa_validation_label || 'Total Claim Amount';
    const items = [
      ['Claim Count', summary.claim_count ?? 0],
      ['Attached LOA', summary.attached_loa_count ?? 0],
      ['Saved Claims', summary.saved_count ?? 0],
      ['For RTP', summary.for_rtp_count ?? 0],
      ['Pending RTP Approval', summary.pending_rtp_final_approval_count ?? summary.missing_rtp_entry_count ?? 0],
      ['Total Claim Amount', formatAmount(summary.total_claim_amount)],
      ['Total RTP Amount', formatAmount(summary.total_rtp_amount)],
      ['SOA Amount', formatAmount(summary.soa_amount)],
      [`Expected SOA (${soaValidationLabel})`, formatAmount(summary.soa_expected_amount ?? summary.computed_soa_total)],
      ['Difference', formatAmount(summary.soa_difference)],
    ];

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(([label, value]) => (
          <div key={label} className="border border-slate-100 rounded-xl p-4 bg-slate-50">
            <div className="text-xs font-semibold text-slate-500 uppercase">{label}</div>
            <div className="mt-1 text-lg font-bold text-slate-800">{value}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderBatchDetail = () => {
    const batch = batchDetail?.batch || {};
    const summary = batchDetail?.summary || {};
    const claims = batchDetail?.claims || [];
    const isLocked = Boolean(summary.is_submitted_locked);

    return (
      <div>
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <button
                onClick={closeBatchDetail}
                className="inline-flex items-center gap-2 mb-3 px-3 py-2 text-sm text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
                Back
              </button>
              <h2 className="text-xl font-bold text-slate-900">{batch.batch_no || selectedBatchNo}</h2>
              <div className="text-sm text-slate-500">
                {batch.provider_name || '-'} | SOA {batch.provider_reference_no || '-'} | Due {formatDate(batch.due_date)}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold border ${getBatchStatusClass(batch.batch_processing_status)}`}>
                {batch.batch_processing_status || 'PENDING'}
              </span>
              <button
                onClick={() => fetchBatchDetail(selectedBatchNo)}
                disabled={batchDetailLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSyncAlt} />
                Refresh
              </button>
              <button
                onClick={handleSubmitBatch}
                disabled={isLocked || batchSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {batchSubmitting ? 'Submitting...' : 'Submit Batch'}
              </button>
            </div>
          </div>
          <div className="mt-5">{renderBatchSummaryCards()}</div>
          {!summary.claim_count_matches && (
            <div className="mt-4 border border-red-200 bg-red-50 text-red-700 rounded-xl p-3 text-sm font-semibold">
              Claim Count does not match the attached LOA count. Please review the batch before continuing.
            </div>
          )}
          {summary.claim_count_matches && summary.soa_amount_matches === false && (
            <div className="mt-4 border border-red-200 bg-red-50 text-red-700 rounded-xl p-3 text-sm font-semibold">
              SOA Amount does not match {summary.soa_validation_label || 'Total Claim Amount'}. Please review the batch before continuing.
            </div>
          )}
          {summary.claim_count_matches && summary.soa_amount_matches !== false && !summary.can_submit && !isLocked && (
            <div className="mt-4 border border-amber-200 bg-amber-50 text-amber-700 rounded-xl p-3 text-sm font-semibold">
              Batch is not ready for submit. Complete saved claims and RTP Entry actions.
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="bg-slate-50">
              <tr>
                {[
                  'Actions',
                  'LOA No.',
                  'TRN No.',
                  'Member',
                  'Coverage',
                  'LOA Amount',
                  'Claim Amount',
                  'RTP Amount',
                  'Status',
                  'Draft',
                  'RTP Entry',
                  'Ready',
                ].map((label) => (
                  <th key={label} className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batchDetailLoading ? (
                Array(6)
                  .fill(null)
                  .map((_, i) => (
                    <tr key={`detail-loading-${i}`} className="animate-pulse">
                      {Array(12)
                        .fill(null)
                        .map((__, idx) => (
                          <td key={idx} className="p-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                          </td>
                        ))}
                    </tr>
                  ))
              ) : claims.length === 0 ? (
                <tr>
                  <td colSpan={12} className="p-6 text-center text-gray-500 text-sm">
                    No claims found.
                  </td>
                </tr>
              ) : (
                claims.map((row) => {
                  const isForProcessing = Number(row.is_for_processing || 0) === 1;
                  const isForRtp = Number(row.is_for_rtp || 0) === 1;
                  const saved = Number(row.batch_processing_saved || 0) === 1;
                  const ready = Number(row.ready_for_batch_submit || 0) === 1;
                  const actionMode = isLocked || !isForProcessing ? 'view' : 'edit';

                  return (
                    <tr key={row.trans_no} className="hover:bg-slate-50">
                      <td className="p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {isForRtp ? (
                            <button
                              onClick={() => openRtpEntry(row)}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
                            >
                              <FontAwesomeIcon icon={faGear} />
                              RTP Entry
                            </button>
                          ) : (
                            <button
                              onClick={() => openBatchClaim(row, actionMode)}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg ${
                                actionMode === 'edit'
                                  ? 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                                  : 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                              }`}
                            >
                              <FontAwesomeIcon icon={actionMode === 'edit' ? faGear : faEye} />
                              {actionMode === 'edit' ? 'Process' : 'View'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm">{row.loa_no || '-'}</td>
                      <td className="p-3 text-sm">{row.trans_no || '-'}</td>
                      <td className="p-3 text-sm">{row.member_name || '-'}</td>
                      <td className="p-3 text-sm">{row.coverage_code || '-'}</td>
                      <td className="p-3 text-sm">{formatAmount(row.loa_amount)}</td>
                      <td className="p-3 text-sm">{formatAmount(row.draft_claim_amount ?? row.claim_amount)}</td>
                      <td className="p-3 text-sm">{formatAmount(row.rtp_amount)}</td>
                      <td className="p-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${getClaimStatusClass(row)}`}>
                          {isForRtp ? 'For RTP' : row.claim_status || '-'}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${
                          saved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}>
                          {saved ? 'Saved' : 'Pending'}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {isForRtp ? (
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${
                            row.latest_rtp_entry_id
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                            {row.latest_rtp_entry_status || 'Pending Entry'}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="p-3 text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${
                          ready ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                          {ready ? 'Ready' : 'Pending'}
                        </span>
                        {!ready && row.blocking_reason ? (
                          <div className="text-xs text-slate-500 mt-1 max-w-xs">{row.blocking_reason}</div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBatchSubmitAlert = () => {
    if (!batchSubmitAlert) return null;

    const blockingRows = Array.isArray(batchSubmitAlert.blockingRows)
      ? batchSubmitAlert.blockingRows
      : [];
    const mismatch = batchSubmitAlert.mismatch;

    return (
      <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </span>
              <div>
                <h3 className="text-lg font-bold text-slate-900">{batchSubmitAlert.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{batchSubmitAlert.message}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeBatchSubmitAlert}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-amber-800 mb-2">Required Before Submit</div>
              <ul className="list-disc pl-5 text-sm text-amber-800 space-y-1">
                {batchSubmitAlert.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>

            {mismatch && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border border-red-100 bg-red-50 rounded-xl p-3">
                  <div className="text-xs font-semibold text-red-600 uppercase">SOA Amount</div>
                  <div className="text-sm font-bold text-red-800">{formatAmount(mismatch.soa_amount)}</div>
                </div>
                <div className="border border-red-100 bg-red-50 rounded-xl p-3">
                  <div className="text-xs font-semibold text-red-600 uppercase">Expected Amount</div>
                  <div className="text-sm font-bold text-red-800">{formatAmount(mismatch.expected_amount)}</div>
                </div>
                <div className="border border-red-100 bg-red-50 rounded-xl p-3">
                  <div className="text-xs font-semibold text-red-600 uppercase">Difference</div>
                  <div className="text-sm font-bold text-red-800">{formatAmount(mismatch.difference)}</div>
                </div>
              </div>
            )}

            {blockingRows.length > 0 && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 text-sm font-semibold text-slate-700">
                  Blocking Rows
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-white border-b border-slate-100">
                      <tr>
                        {['LOA No.', 'TRN No.', 'Claim Status', 'RTP Approval', 'Needed Action'].map((label) => (
                          <th key={label} className="p-3 text-left text-xs font-semibold text-slate-500 uppercase">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {blockingRows.map((row, index) => (
                        <tr key={`${row.trans_no || row.loa_no || index}-${index}`}>
                          <td className="p-3 text-sm text-slate-700">{row.loa_no || '-'}</td>
                          <td className="p-3 text-sm text-slate-700">{row.trans_no || '-'}</td>
                          <td className="p-3 text-sm text-slate-700">{row.claim_status || '-'}</td>
                          <td className="p-3 text-sm text-slate-700">{row.rtp_approval_status || '-'}</td>
                          <td className="p-3 text-sm text-slate-700">{row.blocking_reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
            <button
              type="button"
              onClick={closeBatchSubmitAlert}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700"
            >
              Review Batch
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="max-w-1xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon icon={faSyncAlt} className="text-emerald-600 text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claims Processing</h1>
            <p className="text-sm text-gray-500">Batch and claim processing queues</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="border-b border-gray-100 px-6 pt-6">
            <div className="flex flex-wrap gap-3">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {isBatchTab && selectedBatchNo ? (
            renderBatchDetail()
          ) : (
            <>
              <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="relative flex-1">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder={isBatchTab ? 'Search by Batch No. or Provider' : 'Search by Batch No., LOA No., or TRN No.'}
                      value={searchTerm}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    >
                      {activeSortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          Sort by {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                      className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-emerald-50 transition-all"
                    >
                      <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortAmountUp : faSortAmountDown} />
                      {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    </button>
                    <button
                      onClick={fetchClaims}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-all shadow-sm"
                    >
                      <FontAwesomeIcon icon={faSyncAlt} />
                      Refresh
                    </button>
                    {!isBatchTab && (
                      <button
                        onClick={handleExportCsv}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-slate-700 rounded-xl hover:bg-slate-800 transition-all shadow-sm"
                      >
                        Export CSV
                      </button>
                    )}
                    <button
                      onClick={() => setShowFilters((prev) => !prev)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-all shadow-sm"
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>
                  </div>
                </div>
                {showFilters && renderFilters()}
              </div>

              {isBatchTab ? renderBatchListTable() : renderClaimRowsTable()}
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {!isBatchTab && (
        <ClaimsProcessingDetailsModal
          isOpen={viewModalOpen}
          onClose={closeModals}
          claim={activeClaim}
          formatAmount={formatAmount}
          formatDate={formatDate}
        />
      )}
      <ProcessClaimModal
        isOpen={processModalOpen}
        onClose={closeModals}
        claim={activeClaim}
        mode={processMode}
        showReturnButton={showReturnButton}
        sourceTab={activeTab}
        onSaved={isBatchTab ? handleBatchClaimSaved : null}
      />
      {renderBatchSubmitAlert()}
    </div>
  );
};

export default ClaimsProcessing;
